// People lookup helpers. WeakMap-cached by root.people identity for O(1) lookups.

const _peopleByEmailCache = new WeakMap()
const _emptyMap = Object.freeze({})

const _ensureMap = (people) => {
  if (!people || !people.length) return _emptyMap
  let cached = _peopleByEmailCache.get(people)
  if (cached) return cached
  cached = Object.create(null)
  for (const p of people) {
    if (p && p.email) cached[p.email] = p
  }
  _peopleByEmailCache.set(people, cached)
  return cached
}

// All functions support both direct calls (fn(root, ...)) and el.call('fn', ...) where
// root is inferred from this.state.root when omitted.
export function peopleByEmail (root) {
  const r = root != null ? root : (this && this.state && this.state.root)
  return _ensureMap((r && r.people) || null)
}

// Fuzzy match by email prefix when exact lookup fails. Matches the part
// before `@` against each person's name first-word or email prefix. Used
// so chat messages from aliased emails (e.g. tornike@symbols.app when the
// record is svinchy@symbols.app / Tornike Svintradze) still resolve to the
// person's full name.
const _fuzzyByPrefixCache = new WeakMap()
const _fuzzyMatch = (people, email) => {
  if (!people || !people.length || !email) return null
  const at = email.indexOf('@')
  if (at < 2) return null
  const prefix = email.slice(0, at).toLowerCase()
  const domain = email.slice(at + 1).toLowerCase()
  let cached = _fuzzyByPrefixCache.get(people)
  if (!cached) {
    cached = Object.create(null)
    for (const p of people) {
      if (!p || !p.email) continue
      const pAt = p.email.indexOf('@')
      if (pAt < 0) continue
      const pPrefix = p.email.slice(0, pAt).toLowerCase()
      const pDomain = p.email.slice(pAt + 1).toLowerCase()
      cached[pPrefix + '@' + pDomain] = p
      cached[pPrefix] = p
      const first = firstName(p.name)
      if (first) cached[first.toLowerCase()] = cached[first.toLowerCase()] || p
    }
    _fuzzyByPrefixCache.set(people, cached)
  }
  return cached[prefix + '@' + domain] || cached[prefix] || null
}

// Resolve a person by email. Prefers the tenant-scoped `people` list
// (active-org members only) so cross-tenant lookups don't leak. Falls
// through to `peopleAll` for the caller themselves + any explicit
// lookups that need the full identity directory (e.g. findMe, calendar
// invitee resolution for events that crossed an org boundary).
export function findPerson (rootOrEmail, maybeEmail) {
  if (typeof rootOrEmail === 'string' && maybeEmail === undefined) {
    const root = this && this.state && this.state.root
    if (!root) return null
    const scoped = root.people || null
    const full = root.peopleAll || null
    return _ensureMap(scoped)[rootOrEmail]
      || _fuzzyMatch(scoped, rootOrEmail)
      || _ensureMap(full)[rootOrEmail]
      || _fuzzyMatch(full, rootOrEmail)
      || null
  }
  if (!rootOrEmail || !maybeEmail) return null
  const scoped = rootOrEmail.people || null
  const full = rootOrEmail.peopleAll || null
  return _ensureMap(scoped)[maybeEmail]
    || _fuzzyMatch(scoped, maybeEmail)
    || _ensureMap(full)[maybeEmail]
    || _fuzzyMatch(full, maybeEmail)
    || null
}

export const avatarUrl = (seed) =>
  `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(seed || '?')}`

// Extract the first word of a display name. Shared by chat sidebar group
// labels, greetings on the home page, and the fuzzy-match cache below.
export const firstName = (name, fallback = '') =>
  (name || '').split(/\s+/)[0] || name || fallback

export function avatarUrlFor (root, email) {
  let r = root, e = email
  if (typeof root === 'string' && email === undefined) {
    e = root
    r = this && this.state && this.state.root
  }
  const p = findPerson(r, e)
  return avatarUrl((p && p.name) || e)
}

// Active team members — people with operational roles (member, manager, admin,
// superadmin). Excludes advisors, investors, and shareholders who have left.
// CU-10 §90: roles are no longer on the person object; look up via
// root.roles.byUser[userId]. A person with no role entry defaults to 'member'
// (operational) for backward compatibility with people added before the migration.
const _ACTIVE_ROLES = new Set(['member', 'manager', 'admin', 'superadmin'])
const _activeMembersCache = new WeakMap()
export function activeMembers (root) {
  const r = root != null ? root : (this && this.state && this.state.root)
  const people = (r && r.people) || []
  if (!people.length) return people
  const byUser = r?.roles?.byUser || {}
  // Cache key must incorporate byUser since it changes when roles load.
  // Use a two-key WeakMap trick: cache on people array, invalidate when
  // byUser reference changes via version bump (root.roles.version).
  const version = r?.roles?.version ?? 0
  let cached = _activeMembersCache.get(people)
  if (cached && cached.version === version) return cached.result
  const result = people.filter(p => {
    // Legacy fallback: person still has .roles from a pre-§90 render.
    if (Array.isArray(p.roles) && p.roles.length > 0) {
      return p.roles.some(role => _ACTIVE_ROLES.has(role))
    }
    const assignedRole = byUser[p.userId] || 'member'
    return _ACTIVE_ROLES.has(assignedRole)
  })
  _activeMembersCache.set(people, { version, result })
  return result
}

// People with a specific share_class value.
// Supports both direct call `peopleByShareClass(root, 'advisor')` and
// `el.call('peopleByShareClass', 'advisor')` where root comes from `this`.
export function peopleByShareClass (rootOrCls, maybeCls) {
  let root, cls
  if (maybeCls !== undefined) { root = rootOrCls; cls = maybeCls }
  else { root = this && this.state && this.state.root; cls = rootOrCls }
  return ((root && root.people) || []).filter(p => p.share_class === cls)
}

// True when the viewed person is the current user (no ?u= param or ?u= matches current user).
// Supports el.call('isSelf') and direct isSelf(s).
export function isSelf (s) {
  const state = s != null ? s : (this && this.state)
  const root = state && state.root
  if (!root) return true
  const viewed = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('u')
    : null
  return !viewed || viewed === root.currentUserEmail
}

export const viewedEmail = (s) => {
  const fromQuery = typeof window !== 'undefined'
    ? new URLSearchParams(window.location.search).get('u')
    : null
  return fromQuery || (s && s.root && s.root.currentUserEmail) || ''
}

export const viewedPerson = (s, { allowQuery = false } = {}) => {
  const root = s && s.root
  if (!root) return null
  const email = allowQuery ? viewedEmail(s) : root.currentUserEmail
  return findPerson(root, email)
}
